<template>
    <div class="flex">
        <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div class="www py-2">
                <label
                    for="image-codes"
                    class="block text-sm font-medium text-gray-700 select-none"
                >
                    {{ t('placeholderTypeImageCode') }}
                </label>
                <select
                    id="image-codes"
                    v-model="selectedImageCodeIndex"
                    name="image-codes"
                    class="mt-1 w-full rounded-md border border-gray-300 bg-white font-medium shadow-sm focus:border-blue-500 focus:ring-blue-500 focus:outline-hidden sm:text-sm"
                >
                    <option
                        v-for="(imageCodeType, index) in imageCodeTypes"
                        :key="imageCodeType.code"
                        class="font-medium"
                        :value="index"
                    >
                        {{ imageCodeType.name }}
                    </option>
                </select>
                <div class="mt-4">
                    <ActionButton
                        :value="t('imageCodeGenerateButton')"
                        state="primary"
                        :icon="MdiContentSave"
                        :is-disabled="generating"
                        @click="generateImageCode"
                    />
                </div>
            </div>
            <div class="col-span-1 flex flex-col items-center justify-start md:col-span-2">
                <Transition
                    mode="out-in"
                    @before-enter="beforeInputsEnter"
                    @enter="inputsEnter"
                    @after-enter="afterInputsTransition"
                    @before-leave="beforeInputsLeave"
                    @leave="inputsLeave"
                >
                    <div
                        :key="selectedImageCodeIndex"
                        class="w-full overflow-hidden"
                    >
                        <div
                            v-for="(input, indexKey) in imageCodeTypes[selectedImageCodeIndex]
                                .model"
                            :key="indexKey"
                            class="www py-2"
                        >
                            <label
                                :for="`description-${indexKey}`"
                                class="block text-sm font-medium text-gray-700 select-none"
                            >
                                {{ input.name }}
                            </label>
                            <input
                                :id="`description-${indexKey}`"
                                v-model="input.sample"
                                type="text"
                                :name="`description-${indexKey}`"
                                autocomplete="off"
                                class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 focus:outline-hidden sm:text-sm"
                            />
                        </div>
                    </div>
                </Transition>
            </div>
        </div>
    </div>
</template>
<script setup>
import { ref } from 'vue';
import { t } from '../composables/i18n';
import { getImageCodeTypes } from '../composables/useImageCodes';
import { getImageCodeImage } from '../composables/usePlaceholders';
import ActionButton from '../ui/ActionButton.vue';
import { MdiContentSave } from 'materialdesignicons-vue3/icons/';

defineExpose({
    name: 'InsertImageCodeStatic',
});

const emit = defineEmits(['generated']);

const imageCodeTypes = getImageCodeTypes();
const selectedImageCodeIndex = ref(0);
const generating = ref(false);
const inputsTransitionDuration = 200;
const imageCodeSampleKeyMap = {
    imageCodeVS: 'imageCodeVs',
    imageCodeKS: 'imageCodeKs',
    imageCodeSS: 'imageCodeSs',
};

const clearInputsTransitionStyles = (el) => {
    el.style.height = '';
    el.style.opacity = '';
    el.style.overflow = '';
    el.style.transition = '';
};

const beforeInputsEnter = (el) => {
    el.style.height = '0';
    el.style.opacity = '0';
    el.style.overflow = 'hidden';
};

const inputsEnter = (el, done) => {
    el.style.transition = `height ${inputsTransitionDuration}ms ease, opacity ${inputsTransitionDuration}ms ease`;
    requestAnimationFrame(() => {
        el.style.height = `${el.scrollHeight}px`;
        el.style.opacity = '1';
    });
    setTimeout(done, inputsTransitionDuration);
};

const afterInputsTransition = (el) => {
    clearInputsTransitionStyles(el);
};

const beforeInputsLeave = (el) => {
    el.style.height = `${el.scrollHeight}px`;
    el.style.opacity = '1';
    el.style.overflow = 'hidden';
};

const inputsLeave = (el, done) => {
    el.style.transition = `height ${inputsTransitionDuration}ms ease, opacity ${inputsTransitionDuration}ms ease`;
    requestAnimationFrame(() => {
        el.style.height = '0';
        el.style.opacity = '0';
    });
    setTimeout(done, inputsTransitionDuration);
};

const getSelectedImageCodeType = () => {
    return imageCodeTypes[selectedImageCodeIndex.value];
};

const getImageCodeModelSamples = (imageCodeType) => {
    return Object.entries(imageCodeType.model).reduce((samples, [key, input]) => {
        samples[imageCodeSampleKeyMap[key] || key] = input.sample;
        return samples;
    }, {});
};

const getImageCodePlaceholderData = () => {
    const imageCodeType = getSelectedImageCodeType();
    return {
        sample: [
            {
                imageCodeType: imageCodeType.code,
                imageCodeBarcodeType: imageCodeType.barcodeType,
                imageCodeWidth: imageCodeType.width,
                imageCodeHeight: imageCodeType.height,
                ...getImageCodeModelSamples(imageCodeType),
            },
        ],
    };
};

const blobToDataUrl = (blob) => {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            resolve(reader.result || '');
        };
        reader.readAsDataURL(blob);
    });
};

const normalizeImageCodeResponse = async (imageSrcData) => {
    if (imageSrcData instanceof Blob) {
        return await blobToDataUrl(imageSrcData);
    }

    if (typeof imageSrcData === 'string') {
        return imageSrcData.startsWith('data:')
            ? imageSrcData
            : `data:image/png;base64,${imageSrcData}`;
    }

    return '';
};

const generateImageCode = async () => {
    generating.value = true;

    try {
        const imageCodeType = getSelectedImageCodeType();
        const imageSrcData = await getImageCodeImage(getImageCodePlaceholderData());
        const src = await normalizeImageCodeResponse(imageSrcData);

        if (!src) {
            return;
        }

        emit('generated', {
            src,
            defaultWidth: imageCodeType.width,
            defaultHeight: imageCodeType.height,
            alt: imageCodeType.name,
        });
    } finally {
        generating.value = false;
    }
};
</script>
